import { useState } from 'react';

import { IconCheckDefault } from '@iwoca/icons';
import { Modal } from '@iwoca/orion';
import { Trans, useLingui } from '@lingui/react/macro';

import styles from './AccountCreatedModal.module.css';
import { fetchPutAndGetState } from '../../../api/lendingApi/lendingApiMutations';
import { useGetStateQuery } from '../../../api/lendingApi/lendingApiQueries';
import { TPutStateRequestBody, TRecursiveNonNullable } from '../../../types';
import { Button } from '../../Button/Button';

type TProps = {
  stateKey: string;
};

const AccountCreatedModal = ({ stateKey }: TProps) => {
  const { state, isStateFetching } = useGetStateQuery();

  const [isUpdatingState, setIsUpdatingState] = useState(false);

  const { t } = useLingui();

  const showAccountCreatedModal = !state?.ui?.hasSeenAccountCreatedModal;

  const email = state?.application?.people?.[0].emails?.[0]?.email;
  const hasDirectorRole =
    state?.application?.people?.[0].roles?.includes('director');

  const closeAccountCreatedModal = async () => {
    if (isUpdatingState) return;

    setIsUpdatingState(true);

    const dateNow = new Date();
    dateNow.setMinutes(-1 * dateNow.getTimezoneOffset());

    const newState = {
      data: {
        ...state,
        ui: {
          ...state.ui,
          hasSeenAccountCreatedModal: dateNow.toISOString(),
        },
      },
    } as TPutStateRequestBody;

    if (!hasDirectorRole) {
      const updatedPeople = state.application!
        .people! as TRecursiveNonNullable<TPutStateRequestBody>['data']['application']['people'];

      if (updatedPeople[0].roles) {
        updatedPeople[0].roles.push('director');
      } else {
        updatedPeople[0] = {
          ...updatedPeople[0],
          roles: ['applicant', 'director'],
        };
      }

      newState.data.application!.people = updatedPeople;
    }
    await fetchPutAndGetState({ body: newState, stateKey });

    setIsUpdatingState(false);
  };

  if (!email) {
    return null;
  }

  return (
    <Modal
      className={styles.modalContainer}
      isOpen={showAccountCreatedModal}
      onClose={closeAccountCreatedModal}
      title={t`Your profile has been created`}
      size="medium"
    >
      <div className={styles.modalContents}>
        <p className={styles.setupAccountMessage}>
          <Trans>You can log in to this application with this email:</Trans>
          <span className={`${styles.emailCreated} fs-mask dd-privacy-mask`}>
            {email}
          </span>
        </p>
        <p className={styles.howToSetPasswordMessage}>
          <IconCheckDefault className={styles.icon} fontSize={16} />
          <Trans>We have sent you an email so you can set your password.</Trans>
        </p>
        <Button
          className={styles.closeButton}
          loading={isUpdatingState || isStateFetching}
          onClick={closeAccountCreatedModal}
        >
          <Trans>Got it</Trans>
        </Button>
      </div>
    </Modal>
  );
};

export default AccountCreatedModal;
